/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2018 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import { DEFAULT_THUMBNAIL_ZOOM_LEVEL, HUD_HEIGHT, PROGRESS_BAR_HEIGHT, PAGE_TOPBAR_HEIGHT } from "constants/constants";
import { getPageOrigHeight, getPageOrigWidth } from "selectors/pageSelectors";
import { getDevicePixelRatio } from "dc-preview-utils/src/Utils/DeviceUtils";
import { getTotalPages, isSinglePageMode, isFitOneFullPageMode } from "selectors/documentSelectors";
import { loadStatus, thumbnailOrientationState } from "constants/enums";
import { getViewMode, getIsInPlaceMode } from "selectors/viewModeSelectors";
import { docViewModes, responseSource } from "constants/enums";
import { getViewPortHeight, getViewPortHeightWithoutPagesViewScrollbar } from "selectors/documentViewSelectors";
import { getIsHUDDocked, showProgressBar } from "selectors/hudSelectors";
import { getShouldUseModernViewer } from "selectors/appSelectors";
import { getDefaultThumbnailHeight, getDefaultThumbnailWidth } from "thumbnails/ThumbnailService";

const isValidPageRange = (stores, pageIndex) => pageIndex >= 0 && pageIndex < getTotalPages(stores);

const getPage = (stores, pageIndex) => stores.documentStore.pages[pageIndex];

export const getThumbnailBitmap = (stores, pageIndex) =>
  isValidPageRange(stores, pageIndex) ? getPage(stores, pageIndex).thumbnailBitmap : null;

export const getThumbnailLoadedFrom = (stores, pageIndex) => getPage(stores, pageIndex).thumbnailLoadedFrom;

export const getThumbnailZoomLevelForDCResponse = (stores, pageIndex) => {
  let thumbnailHeight = getPage(stores, pageIndex).thumbnailHeight;
  let thumbnailWidth = getPage(stores, pageIndex).thumbnailWidth;
  let heightZoomLevel =
    thumbnailHeight > 0 ? getDefaultThumbnailHeight(stores) / thumbnailHeight : DEFAULT_THUMBNAIL_ZOOM_LEVEL;
  let widthZoomLevel =
    thumbnailWidth > 0 ? getDefaultThumbnailWidth(stores) / thumbnailWidth : DEFAULT_THUMBNAIL_ZOOM_LEVEL;
  return Math.min(heightZoomLevel, widthZoomLevel);
};

export const getThumbnailOrientation = (stores, pageIndex) => {
  const page = getPage(stores, pageIndex);
  if (!page) return;
  if (page.origWidth > page.origHeight) return thumbnailOrientationState.LANDSCAPE;

  return thumbnailOrientationState.PORTRAIT;
};

export const getThumbnailHeight = (stores, pageIndex) => {
  // thumbnailHeight is response received from AJS/DC which could be fetched at more DPI.
  let thumbnailHeight = getPage(stores, pageIndex).thumbnailHeight;
  let height = thumbnailHeight;

  // thumbnail bitmap from T4 would have dimensions as being requested.
  if (getThumbnailLoadedFrom(stores, pageIndex) === responseSource.AJS) {
    let zoomLevel = getThumbnailZoomLevel(stores, pageIndex);
    let dpi = getDevicePixelRatio();
    if (zoomLevel * dpi > DEFAULT_THUMBNAIL_ZOOM_LEVEL) {
      height = thumbnailHeight;
    } else {
      height = (thumbnailHeight / DEFAULT_THUMBNAIL_ZOOM_LEVEL) * (zoomLevel * dpi);
    }
  }
  // thumbnail bitmap response from DC, can have any any height and width, so map it to available thumbnail area.
  else if (getThumbnailLoadedFrom(stores, pageIndex) === responseSource.DC) {
    height = thumbnailHeight * getThumbnailZoomLevelForDCResponse(stores, pageIndex);
  }
  return height;
};

export const getThumbnailWidth = (stores, pageIndex) => {
  let thumbnailWidth = getPage(stores, pageIndex).thumbnailWidth;
  let width = thumbnailWidth;

  if (getThumbnailLoadedFrom(stores, pageIndex) === responseSource.AJS) {
    let zoomLevel = getThumbnailZoomLevel(stores, pageIndex);
    let dpi = getDevicePixelRatio();
    if (zoomLevel * dpi > DEFAULT_THUMBNAIL_ZOOM_LEVEL) {
      width = thumbnailWidth;
    } else {
      width = (thumbnailWidth / DEFAULT_THUMBNAIL_ZOOM_LEVEL) * (zoomLevel * dpi);
    }
  } else if (getThumbnailLoadedFrom(stores, pageIndex) === responseSource.DC) {
    width = thumbnailWidth * getThumbnailZoomLevelForDCResponse(stores, pageIndex);
  }
  return width;
};

export const getThumbnailZoomLevel = (stores, pageIndex) => {
  let pageHeight = getPageOrigHeight(stores, pageIndex);
  let pageWidth = getPageOrigWidth(stores, pageIndex);
  let heightZoomLevel = pageHeight > 0 ? getDefaultThumbnailHeight(stores) / pageHeight : DEFAULT_THUMBNAIL_ZOOM_LEVEL;
  let widthZoomLevel = pageWidth > 0 ? getDefaultThumbnailWidth(stores) / pageWidth : DEFAULT_THUMBNAIL_ZOOM_LEVEL;
  return Math.min(heightZoomLevel, widthZoomLevel);
};

export const getThumbnailZoomLevelWithDPI = (stores, pageIndex) => {
  let zoomLevel = getThumbnailZoomLevel(stores, pageIndex);
  let dpi = getDevicePixelRatio();
  if (zoomLevel * dpi > DEFAULT_THUMBNAIL_ZOOM_LEVEL) {
    return zoomLevel * dpi;
  }
  // T4 gives blurry layout bitmap for <DEFAULT_THUMBNAIL_ZOOM_LEVEL so for quality rendering at least ask for min. zoomlevel.

  return DEFAULT_THUMBNAIL_ZOOM_LEVEL;
};

export const getThumbnailZoomLevelForAJS = (stores, index) =>
  getThumbnailZoomLevelWithDPI(stores, index) / getDevicePixelRatio();

export const getThumbnailLoadStatus = (stores, pageIndex) => {
  const page = getPage(stores, pageIndex);
  if (page && page.thumbnailLoadStatus) {
    return page.thumbnailLoadStatus;
  }
  return loadStatus.NOT_LOADED;
};

export const getThumbnailBitmapSize = (stores, pageIndex) => getPage(stores, pageIndex).thumbnailBitmapSize;

export const sizeOfThumbnailsLoaded = stores => {
  const totalPages = getTotalPages(stores);
  let totalBitmapSize = 0;
  for (let i = 0; i < totalPages; ++i) {
    if (getThumbnailLoadStatus(stores, i) === loadStatus.LOADED) {
      totalBitmapSize += getThumbnailBitmapSize(stores, i);
    }
  }
  return totalBitmapSize;
};

export const shouldShowThumbnail = stores =>
  (getViewMode(stores) === docViewModes.CLASSIC_VIEW ||
    getViewMode(stores) === docViewModes.NON_PDF ||
    isSinglePageMode(stores) ||
    getIsInPlaceMode(stores) ||
    isFitOneFullPageMode(stores)) &&
  stores.thumbnailStore.shouldShowThumbnailIcon;

export const getThumnailsContainerHeight = stores => {
  const progressBarHeight = showProgressBar(stores) ? PROGRESS_BAR_HEIGHT : 0;
  if (getShouldUseModernViewer(stores)) {
    return getViewPortHeight(stores) - PAGE_TOPBAR_HEIGHT + progressBarHeight;
  }
  const hudHeight = getIsHUDDocked(stores) ? HUD_HEIGHT : 0;
  return getViewPortHeightWithoutPagesViewScrollbar(stores) + hudHeight + progressBarHeight;
};

export const getThumbnailColumnCount = stores => stores.thumbnailStore.thumbnailColumnCount;
